"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import DynamicForm from "./Popup";
import Button from "./Button";
import {
  useGetCityActivityDetails,
  useAddCityActivityDetails,
  useUpdateCityActivityDetails,
  useCityActivityStatus,
  getCityByname,
  useCityActivityImageUpload,
  useAddParkingCharge,
  useAddGuideCharge,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import MapPinPicker from "../MapPinPicker";

interface CityActivity {
  id: number;
  city_id: string;
  title: string;
  description: string;
  image_path: string;
  address: string;
  latitude: string;
  longitude: string;
  created_by: string;
  status: number; 
  city_name: string;
  guide_charge?: string;
  parking_charge?: string;
  include_guide_charge?: number;
  include_parking_charge?: number;
}

interface City {
  city_id: number;
  city_name: string;
  state_name: string;
  country_code: string;
  country_id: number;
  state_id: number;
  latitude: string;
  longitude: string;
  north_east_latitude: string;
  north_east_longitude: string;
  south_west_latitude: string;
  south_west_longitude: string;
  status: number;
}

// Custom form field types
type FormField = {
  id: string;
  label: string;
  type:
    | "text"
    | "email"
    | "password"
    | "select"
    | "checkbox"
    | "textarea"
    | "number"
    | "date"
    | "file"
    | "multiselect"
    | "searchselect"
    | "custom";
  placeholder?: string;
  previewUrl?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  validation?: {
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    customValidator?: (value: string) => boolean;
  };
  defaultValue?: string | boolean | number;
  colSpan?: number;
  loading?: boolean;
  onFileChange?: (file: File) => void;
  currentImage?: string;
  uploadedImage?: string;
  accept?: string;
  // Props for searchselect
  onSearchChange?: (value: string) => void;
  onOptionSelect?: (value: string, label: string) => void;
  selectedValue?: { value: string; label: string } | null;
  // Props for custom type
  render?: React.ReactNode;
};

// Utility function to construct proper image URLs
const constructImageUrl = (imagePath: string) => {
  if (!imagePath) return "";

  const baseUrl = process.env.NEXT_PUBLIC_B2B_URL || "";

  // Remove leading slash from imagePath if baseUrl already ends with slash
  let cleanImagePath = imagePath;
  if (baseUrl.endsWith("/") && imagePath.startsWith("/")) {
    cleanImagePath = imagePath.substring(1);
  } else if (!baseUrl.endsWith("/") && !imagePath.startsWith("/")) {
    cleanImagePath = "/" + imagePath;
  }

  return `${baseUrl}${cleanImagePath}`;
};

const CityActivityMaster: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<CityActivity | null>(
    null
  );
  const [activities, setActivities] = useState<CityActivity[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [cities, setCities] = useState<City[]>([]);
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [key, setKey] = useState(0); // Key to force re-render of DynamicForm

  // API Hooks
  const getCityActivities = useGetCityActivityDetails();
  const addCityActivity = useAddCityActivityDetails();
  const updateCityActivity = useUpdateCityActivityDetails();
  const updateStatus = useCityActivityStatus();
  const addParkingCharge = useAddParkingCharge();
  const addGuideCharge = useAddGuideCharge();
  const imageUploadMutation = useCityActivityImageUpload(
    (filePath, url) => {
      const fullImageUrl = constructImageUrl(filePath);
      setUploadedImageUrl(fullImageUrl);
    },
    (error) => {
      alert(error.message || "Failed to upload image. Please try again.");
    }
  );

  const citiesQuery = getCityByname(debouncedSearchTerm);
  const user = useAuth();

  // Fetch city activities on component mount
  useEffect(() => {
    fetchCityActivities();
  }, []);

  const fetchCityActivities = () => {
    getCityActivities.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setActivities(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching city activities:", error);
        },
      }
    );
  };

  // Debounce search term for city search
  useEffect(() => {
    if (citySearchTerm.length >= 3) {
      const handler = setTimeout(() => {
        setDebouncedSearchTerm(citySearchTerm);
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setCities([]);
      setDebouncedSearchTerm("");
    }
  }, [citySearchTerm]);

  // Update cities when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm && citiesQuery.data) {
      const citiesData: any = citiesQuery.data;
      if (citiesData.data && Array.isArray(citiesData.data)) {
        setCities(citiesData.data);
      } else {
        setCities([]);
      }
    } else {
      setCities([]);
    }
  }, [debouncedSearchTerm, citiesQuery.data]);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    const tempUrl = URL.createObjectURL(file);
    setUploadedImageUrl(tempUrl);

    imageUploadMutation.mutate(file, {
      onSuccess: (data) => {
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
      },
      onError: (error) => {
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
        setUploadedImageUrl("");
      },
    });
  };

  // Handle MapPinPicker changes
  const handleMapChange = (lat: number, lng: number, mapAddress: string, pincode: string, city: string) => {
    setLatitude(lat.toString());
    setLongitude(lng.toString());
    // If user hasn't manually entered an address, use the map's reverse geocoded address
    if (!address && mapAddress) {
      setAddress(mapAddress);
    }
  };

  // Handle input changes
  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setUploadedImageUrl("");
      setCurrentActivity(null);
      setSelectedCity(null);
      setCitySearchTerm("");
      setDebouncedSearchTerm("");
      setCities([]);
      setAddress("");
      setLatitude("");
      setLongitude("");
      setTitle("");
      setDescription("");
      setKey(prev => prev + 1); // Force re-render on close
    }
  }, [isModalOpen]);

  // When currentActivity changes (when editing), update all form fields
  useEffect(() => {
    if (currentActivity && isModalOpen) {
      setSelectedCity({
        id: currentActivity.city_id.toString(),
        name: currentActivity.city_name,
      });
      setCitySearchTerm(currentActivity.city_name);
      setAddress(currentActivity.address);
      setLatitude(currentActivity.latitude);
      setLongitude(currentActivity.longitude);
      setTitle(currentActivity.title);
      setDescription(currentActivity.description);
      setKey(prev => prev + 1); // Force re-render when editing
    }
  }, [currentActivity, isModalOpen]);

  // Build form fields with current values
  const formFields: FormField[] = [
    {
      id: "city_search",
      label: "Destination City *",
      type: "searchselect",
      required: true,
      defaultValue: citySearchTerm,
      placeholder: "Type at least 3 characters to search for city...",
      options: cities.map((city) => ({
        value: city.city_id.toString(),
        label: `${city.city_name}, ${city.state_name}, ${city.country_code}`,
      })),
      loading: citiesQuery.isLoading,
      onSearchChange: (value: string) => setCitySearchTerm(value),
      onOptionSelect: (value: string, label: string) => {
        const selectedCityData = cities.find(
          (city) => city.city_id.toString() === value
        );
        if (selectedCityData) {
          setSelectedCity({
            id: value,
            name: selectedCityData.city_name,
          });
          setCitySearchTerm(selectedCityData.city_name);
        }
        setCities([]);
      },
      selectedValue: selectedCity
        ? { value: selectedCity.id, label: selectedCity.name }
        : null,
    },
    {
      id: "title",
      label: "Title *",
      type: "text",
      required: true,
      placeholder: "Enter activity title",
      defaultValue: title,
      onSearchChange: handleTitleChange,
    },
    {
      id: "description",
      label: "Description *",
      type: "textarea",
      required: true,
      placeholder: "Enter activity description",
      defaultValue: description,
      onSearchChange: handleDescriptionChange,
    },
    {
      id: "address",
      label: "Address *",
      type: "textarea",
      required: true,
      placeholder: "Enter activity address",
      defaultValue: address,
      onSearchChange: handleAddressChange,
    },
    {
      id: "image_path",
      label: "Activity Image",
      type: "file",
      required: false,
      defaultValue: currentActivity ? currentActivity.image_path : "",
      accept: "image/*",
      loading: imageUploadMutation.isPending,
      onFileChange: handleFileUpload,
      currentImage: currentActivity?.image_path
        ? constructImageUrl(currentActivity.image_path)
        : "",
      uploadedImage: uploadedImageUrl,
    },
    {
      id: "guide_charge",
      label: "Guide Charge",
      type: "number",
      required: false,
      placeholder: "Enter guide charge",
      defaultValue: currentActivity?.guide_charge || "",
    },
    {
      id: "include_guide_charge",
      label: "Include Guide Charge",
      type: "checkbox",
      required: false,
      defaultValue: currentActivity
        ? currentActivity.include_guide_charge === 1
        : undefined,
    },
    {
      id: "parking_charge",
      label: "Parking Charge",
      type: "number",
      required: false,
      placeholder: "Enter parking charge",
      defaultValue: currentActivity?.parking_charge || "",
    },
    {
      id: "include_parking_charge",
      label: "Include Parking Charge",
      type: "checkbox",
      required: false,
      defaultValue: currentActivity
        ? currentActivity.include_parking_charge === 1
        : undefined,
    },
    {
      id: "coordinates",
      label: "Location Coordinates *",
      type: "custom",
      required: true,
      render: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Location on Map *
            </label>
            <div className="border border-gray-300 rounded-md p-2">
              <MapPinPicker
                onChange={handleMapChange}
                initialLat={
                  latitude ? parseFloat(latitude) : 
                  currentActivity?.latitude ? parseFloat(currentActivity.latitude) : 28.6139
                }
                initialLng={
                  longitude ? parseFloat(longitude) : 
                  currentActivity?.longitude ? parseFloat(currentActivity.longitude) : 77.209
                }
                initialZoom={12}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Latitude *
              </label>
              <input
                type="text"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="Enter latitude or click on map"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Longitude *
              </label>
              <input
                type="text"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="Enter longitude or click on map"
              />
            </div>
          </div>
          
          <p className="text-xs text-gray-500">
            Click on the map or drag the marker to set the exact location, or manually enter coordinates above.
          </p>
        </div>
      ),
    },
  ];

  const handleFormSubmit = async (formData: any) => {
    const loggedInUser = user?.user?.id;

    if (!selectedCity) {
      alert("Please select a city");
      return;
    }

    // Use the state values instead of formData for fields we're managing separately
    const finalData = {
      ...formData,
      title: title || formData.title,
      description: description || formData.description,
      address: address || formData.address,
      latitude: latitude || formData.latitude,
      longitude: longitude || formData.longitude,
    };

    if (!finalData.address) {
      alert("Please enter an address");
      return;
    }

    if (!finalData.latitude || !finalData.longitude) {
      alert("Please enter latitude and longitude coordinates");
      return;
    }

    try {
      let imagePath = "";
      if (uploadedImageUrl) {
        const url = new URL(uploadedImageUrl);
        imagePath = url.pathname;
      } else if (currentActivity?.image_path) {
        imagePath = currentActivity.image_path;
      }

      const payload = {
        city_id: selectedCity.id,
        title: finalData.title,
        description: finalData.description,
        image_path: imagePath,
        address: finalData.address,
        latitude: finalData.latitude,
        longitude: finalData.longitude,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentActivity) {
        // Update existing activity - keep existing status
        updateCityActivity.mutate(
          {
            ...payload,
            auto_id: currentActivity.id,
            status: currentActivity.status, // Keep existing status
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchCityActivities();
                setIsModalOpen(false);
                setCurrentActivity(null);
                setUploadedImageUrl("");
              }
            },
            onError: (error) => {
              alert("Failed to update city activity. Please try again.");
            },
          }
        );
      } else {
        // Add new activity - default status is 1 (Active)
        const shouldAddParkingCharge =
          finalData.parking_charge &&
          Number(finalData.parking_charge) > 0 &&
          finalData.include_parking_charge;

        const shouldAddGuideCharge =
          finalData.guide_charge &&
          Number(finalData.guide_charge) > 0 &&
          finalData.include_guide_charge;

        addCityActivity.mutate(
          {
            ...payload,
            status: 1, // Default status as 1 (Active)
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                const newActivityId = data.data;

                const finalize = () => {
                  fetchCityActivities();
                  setIsModalOpen(false);
                  setUploadedImageUrl("");
                };

                const runGuideCharge = () => {
                  if (shouldAddGuideCharge && newActivityId) {
                    const guidePayload = {
                      ref_id: newActivityId,
                      type: "ACTIVITY",
                      guide_cost: Number(finalData.guide_charge),
                      ip: "127.0.0.1",
                      created_by: loggedInUser,
                      modified_by: loggedInUser,
                      status: 1,
                    };

                    addGuideCharge.mutate(guidePayload, {
                      onSuccess: () => {
                        finalize();
                      },
                      onError: () => {
                        alert(
                          "City activity created but failed to add guide charge."
                        );
                        finalize();
                      },
                    });
                  } else {
                    finalize();
                  }
                };

                if (shouldAddParkingCharge && newActivityId) {
                  const parkingPayload = {
                    ref_id: newActivityId,
                    type: "ACTIVITY",
                    amount: Number(finalData.parking_charge),
                    ip: "127.0.0.1",
                    created_by: loggedInUser,
                    modified_by: loggedInUser,
                    status: 1,
                  };

                  addParkingCharge.mutate(parkingPayload, {
                    onSuccess: () => {
                      runGuideCharge();
                    },
                    onError: () => {
                      alert(
                        "City activity created but failed to add parking charge."
                      );
                      runGuideCharge();
                    },
                  });
                } else {
                  runGuideCharge();
                }
              }
            },
            onError: (error) => {
              alert("Failed to add city activity. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchCityActivities();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchCityActivities();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    if (confirm("Are you sure you want to delete selected city activities?")) {
      
      setSelectedRows([]);
    }
  };

  // Transform API data for table structure
  const tableData = activities.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    city: item.city_name,
    title: item.title,
    description: item.description,
    address: item.address,
    latitude: item.latitude,
    longitude: item.longitude,
    createdBy: item.created_by,
    status: item.status,
    image: item.image_path,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    {
      key: "image",
      header: "Image",
      render: (item: any) => {
        if (!item) {
          return (
            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-xs text-gray-500">No Image</span>
            </div>
          );
        }

        const imageUrl = constructImageUrl(item);

        return (
          <div className="w-12 h-12 rounded overflow-hidden">
            <img
              src={imageUrl}
              alt="Activity"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'%3E%3C/path%3E%3C/svg%3E";
              }}
            />
          </div>
        );
      },
    },
    { key: "city", header: "City", sortable: true },
    { key: "title", header: "Title", sortable: true },
    {
      key: "description",
      header: "Description",
      sortable: true,
      width: "300px",
    },
    { key: "address", header: "Address", sortable: true },
    { key: "latitude", header: "Latitude", sortable: true },
    { key: "longitude", header: "Longitude", sortable: true },
    { key: "createdBy", header: "Created By", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentActivity(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (
                  confirm("Are you sure you want to delete this city activity?")
                ) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentActivity(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add City Activity
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={
            getCityActivities.isPending || imageUploadMutation.isPending
          }
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Latitude
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Longitude
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentActivity(null);
              setUploadedImageUrl("");
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentActivity
                    ? "Update City Activity"
                    : "Add City Activity"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentActivity(null);
                    setUploadedImageUrl("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  key={key} // Add key to force re-render when values change
                  fields={formFields}
                  onSubmit={handleFormSubmit}
                  submitText={
                    currentActivity
                      ? "Update City Activity"
                      : "Add City Activity"
                  }
                  loading={
                    addCityActivity.isPending ||
                    updateCityActivity.isPending ||
                    imageUploadMutation.isPending
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityActivityMaster;



